// resolveDrop (IMPLEMENTATION §2.5, §2.7 "Drop resolution order"): ONE pure function that serves both the
// per-frame preview (query.resolveDrop) and the authoritative apply (commandSystem `drop`). It never
// mutates. Payloads:
//   item (a loose item, or an item dragged out of a machine slot / out buffer / Packer buffer / Gallery
//   queue): same cell no-op → identical below max: merge → identical at max: swap + MAX → ghost needs it:
//   deliver → building accepts it now: insert → building refuses: reason → other loose item: swap (a slot
//   item cannot swap: `occupied`) → empty floor: move → counter / Sell tool: sale.
//   building (Build mode only): same type and level → bmerge (level +1, target keeps cell and facing) →
//   empty floor: bmove → loose item: bmove, the item swaps to the origin → other building: bswap → Sell
//   tool: bsell (100% refund; a ghost is cancelled). Chutes are painted/erased, never dragged (`wired`).
//   shop card: empty floor → buyPlace (a ghost when it is the first build of its type; facing by the
//   placement assist) → identical L1 building → buyMerge (deposits in either mode, other buildings in
//   Build) → otherwise `occupied`.

import { maxLevelOf, shopItem } from '../bdefs.ts';
import type { Dst, RejectReason, Src } from '../commands.ts';
import { applyPermille } from '../core/num.ts';
import type { Dir, Int } from '../core/types.ts';
import type { Ctx } from '../ctx.ts';
import type { SimDefs } from '../defs.ts';
import { famDef, findBuilding, findItem, itemValue } from '../entities.ts';
import type { BType, FamId, OreId, ShopId } from '../gen/ids.ts';
import { ghostFill, isGhostBuild } from '../ghosts.ts';
import { isOpen, type Occ, occAt } from '../occupancy.ts';
import type { Building, ItemRef, SimState } from '../state.ts';
import { reservedAt } from '../topology.ts';
import { hasEffect } from '../unlocks.ts';
import { acceptReason } from './accept.ts';
import { autoFacing } from './place.ts';
import { priceOf, shopBlock } from './price.ts';

export type DropOutcome =
  /** Released on its own cell: nothing happens. */
  | { k: 'none' }
  | { k: 'move'; id: Int; x: Int; y: Int }
  /** a (dragged) + b (target) → one item of `tier` on b's cell. */
  | { k: 'merge'; a: Int; b: Int; fam: FamId; tier: Int; x: Int; y: Int }
  /** a goes to b's cell, b to a's origin. `max`: identical twins at max tier (the "MAX" tag). */
  | { k: 'swap'; a: Int; b: Int; max: boolean }
  | { k: 'sell'; id: Int; value: Int; where: 'counter' | 'sell' }
  /** The item enters building `bid` (hand insert). */
  | { k: 'insert'; id: Int; bid: Int }
  /** The item fills `fills` requirement slots of ghost `bid`; `change` is owed until it completes. */
  | { k: 'deliver'; id: Int; bid: Int; fills: Int; change: Int }
  | {
      k: 'buyPlace';
      item: ShopId;
      type: BType;
      ore: OreId | null;
      x: Int;
      y: Int;
      dir: Dir;
      price: Int;
      ghost: boolean;
    }
  /** Buy one more copy and merge it into L1 building `bid` (→ L2). */
  | { k: 'buyMerge'; item: ShopId; bid: Int; price: Int; level: Int }
  /** Building `bid` moves to (x, y); `item` (0 = none) is the loose item that moves to its origin. */
  | { k: 'bmove'; bid: Int; x: Int; y: Int; item: Int }
  /** Building `bid` merges into `into` (which keeps its cell and facing) → `level`. */
  | { k: 'bmerge'; bid: Int; into: Int; level: Int }
  | { k: 'bswap'; a: Int; b: Int }
  /** Build Sell tool: `refund` = 100% of paid; `ghost` = a ghost is cancelled (items come back too). */
  | { k: 'bsell'; bid: Int; refund: Int; ghost: boolean }
  | { k: 'reject'; reason: RejectReason };

function no(reason: RejectReason): DropOutcome {
  return { k: 'reject', reason };
}

/** True when `id` is the last item of its family anywhere (floor, stored items, mounted guns). */
function isLastOfFamily(s: SimState, fam: FamId, id: Int): boolean {
  for (const o of s.floor) if (o.fam === fam && o.id !== id) return false;
  for (const b of s.buildings)
    if (b.type === 'gallery' && b.gun && b.gun.fam === fam && b.gun.id !== id) return false;
  return true;
}

/** Counter / Sell-tool sale of an item (R13 sale; D5: 100%). */
export function saleOutcome(
  s: SimState,
  d: SimDefs,
  it: ItemRef,
  where: 'counter' | 'sell',
): DropOutcome {
  if (!hasEffect(s, d, where === 'counter' ? 'counter' : 'buildMode')) return no('notUnlocked');
  const f = famDef(d, it.fam);
  if (f.protect === 'all' || (f.protect === 'last' && isLastOfFamily(s, it.fam, it.id))) {
    return no(f.kind === 'gun' ? 'pinnedGun' : 'pinned');
  }
  const value = applyPermille(itemValue(d, it.fam, it.tier), d.economy.sellPermille.counter);
  return { k: 'sell', id: it.id, value, where };
}

/** The item payload: a loose item or a stored (slot) item, with the cell it is dragged from. */
interface Payload {
  item: ItemRef;
  x: Int;
  y: Int;
  /** From a building's slot / out buffer / queue. */
  stored: boolean;
}

function storedPayload(s: SimState, from: Extract<Src, { k: 'slot' }>): Payload | null {
  const b = findBuilding(s, from.bid);
  if (b === null || b.ghost !== null) return null;
  let list: readonly ItemRef[] | null = null;
  switch (b.type) {
    case 'caster':
    case 'grinder':
    case 'smelter':
    case 'press':
    case 'loader':
    case 'gunsmith':
      list = from.slot < 0 ? b.out : (b.slots[from.slot] ?? null);
      break;
    case 'packer':
      list = from.slot < 0 ? b.out : from.slot === 0 ? b.buf : null;
      break;
    case 'gallery':
      list = from.slot === 0 ? b.queue : null;
      break;
    default:
      list = null;
  }
  if (list === null || !Number.isInteger(from.idx)) return null;
  const it = list[from.idx];
  if (it === undefined || it.id !== from.id) return null;
  return { item: { id: it.id, fam: it.fam, tier: it.tier }, x: b.x, y: b.y, stored: true };
}

function itemDrop(
  s: SimState,
  d: SimDefs,
  occ: Occ,
  p: Payload,
  to: Dst,
  build: boolean,
): DropOutcome {
  const it = p.item;
  switch (to.k) {
    case 'counter':
      return saleOutcome(s, d, it, 'counter');
    case 'sell':
      return build ? saleOutcome(s, d, it, 'sell') : no('buildOnly');
    case 'gunSlot':
      return no('notAccepted'); // v0.2 mount
    case 'cell':
      break;
  }
  if (!isOpen(occ, to.x, to.y)) return no('locked');
  if (to.x === p.x && to.y === p.y) return { k: 'none' };
  const id = occAt(occ, to.x, to.y);
  if (id === 0) return { k: 'move', id: it.id, x: to.x, y: to.y };
  const other = findItem(s, id);
  if (other !== null) {
    if (other.fam === it.fam && other.tier === it.tier) {
      if (it.tier < famDef(d, it.fam).maxTier) {
        return {
          k: 'merge',
          a: it.id,
          b: other.id,
          fam: it.fam,
          tier: it.tier + 1,
          x: other.x,
          y: other.y,
        };
      }
      return p.stored ? no('occupied') : { k: 'swap', a: it.id, b: other.id, max: true };
    }
    return p.stored ? no('occupied') : { k: 'swap', a: it.id, b: other.id, max: false };
  }
  const b = findBuilding(s, id);
  if (b === null) return no('stale');
  if (b.ghost !== null) {
    const fill = ghostFill(d, b, it.fam, it.tier);
    return fill === null
      ? no('notAccepted')
      : { k: 'deliver', id: it.id, bid: b.id, fills: fill.k, change: fill.change };
  }
  // a read-only context: acceptance counts the items in transit to `b` (R8 reservations, topology.ts)
  const c: Ctx = { s, d, ev: [], occ, topo: null };
  const why = acceptReason(c, b, it.fam, it.tier, null, reservedAt(c, b.x, b.y));
  return why === null ? { k: 'insert', id: it.id, bid: b.id } : no(why);
}

function sameKind(a: Building, b: Building): boolean {
  if (a.type !== b.type) return false;
  return a.type !== 'deposit' || (b.type === 'deposit' && a.ore === b.ore);
}

function buildingDrop(
  s: SimState,
  d: SimDefs,
  occ: Occ,
  b: Building,
  to: Dst,
  build: boolean,
): DropOutcome {
  if (!build) return no('buildOnly');
  if (!hasEffect(s, d, 'buildMode')) return no('notUnlocked');
  if (b.type === 'chute') return no('wired');
  switch (to.k) {
    case 'sell':
      return bsellOutcome(s, d, b);
    case 'counter':
    case 'gunSlot':
      return no('notAccepted');
    case 'cell':
      break;
  }
  if (!isOpen(occ, to.x, to.y)) return no('locked');
  if (to.x === b.x && to.y === b.y) return { k: 'none' };
  const id = occAt(occ, to.x, to.y);
  if (id === 0) return { k: 'bmove', bid: b.id, x: to.x, y: to.y, item: 0 };
  if (findItem(s, id) !== null) return { k: 'bmove', bid: b.id, x: to.x, y: to.y, item: id };
  const t = findBuilding(s, id);
  if (t === null) return no('stale');
  if (t.type === 'chute') return no('wired');
  if (sameKind(b, t) && b.level === t.level) {
    if (b.ghost !== null || t.ghost !== null) return no('occupied');
    if (t.level >= maxLevelOf(d, t)) return no('max');
    if (t.type === 'gallery' && !hasEffect(s, d, 'gallery.merge')) return no('notUnlocked');
    return { k: 'bmerge', bid: b.id, into: t.id, level: t.level + 1 };
  }
  return { k: 'bswap', a: b.id, b: t.id };
}

/** Selling a building with the Build Sell tool (R14/R15): 100% of paid; protected ones are pinned. */
export function bsellOutcome(s: SimState, d: SimDefs, b: Building): DropOutcome {
  if (b.ghost !== null) return { k: 'bsell', bid: b.id, refund: b.paid, ghost: true };
  if (isProtected(s, d, b)) return no('pinned');
  return {
    k: 'bsell',
    bid: b.id,
    refund: applyPermille(b.paid, d.economy.buildRefundPermille),
    ghost: false,
  };
}

/** R15: the last Lead Vein and the last Gallery (v0.1: the one Gallery) can't be sold. */
export function isProtected(s: SimState, d: SimDefs, b: Building): boolean {
  let protectLast = false;
  if (b.type === 'deposit') {
    for (const x of d.deposits) if (x.id === b.ore) protectLast = x.protectLast;
  } else {
    protectLast = d.buildings[b.type].protectLast;
  }
  if (!protectLast) return false;
  for (const o of s.buildings)
    if (o.id !== b.id && o.ghost === null && sameKind(o, b)) return false;
  return true;
}

function shopDrop(
  s: SimState,
  d: SimDefs,
  occ: Occ,
  id: ShopId,
  to: Dst,
  build: boolean,
): DropOutcome {
  const block = shopBlock(s, d, id);
  if (block !== null) return no(block);
  const item = shopItem(d, id);
  if (item.ref === 'chute') return no('wired'); // chutes are painted with the Build Chute tool
  if (to.k !== 'cell') return no('notAccepted');
  if (!isOpen(occ, to.x, to.y)) return no('locked');
  const price = priceOf(s, d, id);
  const type: BType = item.kind === 'deposit' ? 'deposit' : (item.ref as BType);
  const ore: OreId | null = item.kind === 'deposit' ? (item.ref as OreId) : null;
  const at = occAt(occ, to.x, to.y);
  if (at === 0) {
    if (price === null) return no('max');
    if (s.wallet.cash < price) return no('noCash');
    return {
      k: 'buyPlace',
      item: id,
      type,
      ore,
      x: to.x,
      y: to.y,
      dir: type === 'deposit' ? 'N' : autoFacing(s, d, occ, type, to.x, to.y),
      price,
      ghost: isGhostBuild(s, d, type),
    };
  }
  const t = findBuilding(s, at);
  if (t === null || t.ghost !== null || t.level !== 1 || t.type !== type) return no('occupied');
  if (t.type === 'deposit' && t.ore !== ore) return no('occupied');
  if (type !== 'deposit' && !build) return no('buildOnly');
  if (t.level >= maxLevelOf(d, t)) return no('max');
  if (type === 'gallery' && !hasEffect(s, d, 'gallery.merge')) return no('notUnlocked');
  if (price === null) return no('max');
  if (s.wallet.cash < price) return no('noCash');
  return { k: 'buyMerge', item: id, bid: t.id, price, level: t.level + 1 };
}

export function resolveDrop(
  s: SimState,
  d: SimDefs,
  occ: Occ,
  from: Src,
  to: Dst,
  build: boolean,
): DropOutcome {
  if (typeof from !== 'object' || from === null || typeof to !== 'object' || to === null) {
    return no('stale');
  }
  if (from.k === 'shop') return shopDrop(s, d, occ, from.item, to, build);
  if (from.k === 'slot') {
    const p = storedPayload(s, from);
    return p === null ? no('stale') : itemDrop(s, d, occ, p, to, build);
  }
  if (from.k !== 'cell') return no('stale'); // v0.2 tray
  const it = findItem(s, from.id);
  if (it === null) {
    const b = findBuilding(s, from.id);
    if (b !== null && b.x === from.x && b.y === from.y)
      return buildingDrop(s, d, occ, b, to, build);
    return no('stale');
  }
  if (it.x !== from.x || it.y !== from.y) return no('stale');
  const p: Payload = { item: it, x: it.x, y: it.y, stored: false };
  return itemDrop(s, d, occ, p, to, build);
}
